/**
 * ApplicationTab Component (seeds)
 *
 * Application-level view for web-application seeds. Provides a left sub-nav to
 * switch between the Sitemap and the REST API Endpoints tables. Both tables are
 * reused from the asset section (fed the seed's key/identifier/details_path),
 * mirroring the asset drawer's Attributes → Application layout.
 *
 * @component
 */

import { CodeBracketIcon, ServerStackIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';

import { StatusChip } from '@/components/ui/status-chip';
import { WebApplicationEndpoints } from '@/sections/asset/components/tabs';
import { SitemapTab } from '@/sections/seeds/components/drawer/tabs/SitemapTab';
import { Seed } from '@/types';
import { cn } from '@/utils/classname';

interface ApplicationTabProps {
  /** The web application seed to display application content for. */
  seed: Seed;
}

type SubView = 'sitemap' | 'endpoints';

const SUB_VIEWS: {
  value: SubView;
  label: string;
  icon: typeof ServerStackIcon;
}[] = [
  { value: 'sitemap', label: 'Sitemap', icon: ServerStackIcon },
  { value: 'endpoints', label: 'REST API Endpoints', icon: CodeBracketIcon },
];

export function ApplicationTab({ seed }: ApplicationTabProps) {
  const [subView, setSubView] = useState<SubView>('sitemap');
  const activeLabel =
    SUB_VIEWS.find(item => item.value === subView)?.label ?? '';

  return (
    <div className="flex h-full w-full">
      {/* Left sub-nav — mirrors the asset Attributes → Application sidebar. */}
      <div className="border-chariot-stroke w-48 shrink-0 overflow-y-auto border-r">
        {SUB_VIEWS.map(item => {
          const selected = subView === item.value;
          return (
            <button
              key={item.value}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => setSubView(item.value)}
              className={cn(
                'border-chariot-stroke flex w-full items-center gap-2 rounded-none border-y px-3 py-2 text-left text-sm font-medium transition-colors',
                selected
                  ? 'bg-chariot-background-secondary text-chariot-text-primary shadow-xs'
                  : 'text-chariot-text-secondary hover:bg-chariot-background-secondary hover:text-chariot-text-primary'
              )}
            >
              <item.icon
                className={cn(
                  'size-4 shrink-0',
                  selected
                    ? 'text-chariot-text-primary'
                    : 'text-chariot-text-secondary'
                )}
              />
              <span>{item.label}</span>
            </button>
          );
        })}
      </div>

      {/* Selected sub-view content — title + subtitle mirror the asset
          Attributes → Application sub-view header (same styling + padding).
          px-6 matches the asset content pane so the action pills sit inset from
          the right edge instead of touching it. */}
      <div className="flex-1 overflow-y-auto px-6 py-4">
        <div className="flex h-full flex-col gap-6">
          <div className="border-chariot-stroke pb-4">
            <div className="flex items-center justify-between">
              <h2 className="text-chariot-text-primary text-xl font-semibold">
                {activeLabel}
              </h2>
              {subView === 'endpoints' && (
                <StatusChip label="Discovered by vespasian" variant="info" />
              )}
            </div>
            <p className="text-chariot-text-secondary text-sm">
              {activeLabel} attributes and configuration details
            </p>
          </div>
          {subView === 'sitemap' ? (
            <SitemapTab seed={seed} />
          ) : (
            <WebApplicationEndpoints
              asset={{
                key: seed.key,
                identifier: seed.identifier,
                details_path: seed.details_path,
                visited: seed.visited,
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
